import React, { FC, useState, useEffect, useMemo } from 'react'
import { Modal, Button, Input, Switch } from 'antd'
import {
  ExclamationCircleOutlined,
  RightOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import {
  ComplexAvatarContainer,
  CrudeAvatar,
  useStateContext,
  useTranslation,
} from '../../../common'
import GroupDetail from './GroupDetail'
import GroupList from './GroupList'
import GroupPower from './GroupPower'
import { GroupSettingType } from '../../types'
import { GroupItemProps } from './GroupItem'
import {
  V2NIMTeam,
  V2NIMTeamMember,
  V2NIMUpdateTeamInfoParams,
  V2NIMUpdateSelfMemberInfoParams,
} from 'nim-web-sdk-ng/dist/esm/nim/src/V2NIMTeamService'
import { V2NIMConst } from 'nim-web-sdk-ng/dist/esm/nim'
import { isDiscussionFunc } from '../../../utils'

export interface HistoryStack {
  path: GroupSettingType
  title: string
}

export interface ChatTeamSettingProps {
  members: V2NIMTeamMember[]
  team: V2NIMTeam
  teamDoNotDisturbMode: number
  myAccount: string
  isGroupOwner: boolean
  isGroupManager: boolean
  navHistoryStack: HistoryStack[]

  onDismissTeam: () => void
  onLeaveTeam: () => void
  onAddMembersClick: () => void
  onTransferTeamClick: () => void
  onRemoveTeamMemberClick: (member: V2NIMTeamMember) => void
  onUpdateTeamInfo: (team: V2NIMUpdateTeamInfoParams) => void
  onUpdateMyMemberInfo: (params: V2NIMUpdateSelfMemberInfoParams) => void
  onTeamMuteChange: (mute: boolean) => void
  onLeaveDiscussion?: () => void
  onTeamDisturbChange: (disturb: boolean) => void
  afterSendMsgClick?: () => void
  setNavHistoryStack: (stack: HistoryStack[]) => void
  renderTeamMemberItem?: (
    params: GroupItemProps & {
      renderKey: string
      renderIndex: number
      renderStyle: React.CSSProperties
    }
  ) => JSX.Element | null | undefined

  prefix?: string
  commonPrefix?: string
}

const { confirm } = Modal

const ChatTeamSetting: FC<ChatTeamSettingProps> = ({
  members,
  team,
  teamDoNotDisturbMode,
  myAccount,
  isGroupOwner,
  isGroupManager,
  navHistoryStack,
  onDismissTeam,
  onLeaveDiscussion,
  onLeaveTeam,
  onAddMembersClick,
  onTransferTeamClick,
  onRemoveTeamMemberClick,
  onUpdateTeamInfo,
  onUpdateMyMemberInfo,
  onTeamMuteChange,
  onTeamDisturbChange,
  afterSendMsgClick,
  setNavHistoryStack,
  renderTeamMemberItem,

  prefix = 'chat',
  commonPrefix = 'common',
}) => {
  const { localOptions } = useStateContext()
  const { t } = useTranslation()
  const _prefix = `${prefix}-group-setting`
  const [nickInTeam, setNickInTeam] = useState('')

  const path = navHistoryStack[navHistoryStack.length - 1]?.path || 'home'
  // 是否是讨论组
  const isDiscussion = useMemo(() => {
    return isDiscussionFunc(team.serverExtension)
  }, [team.serverExtension])

  const GROUP_SETTING_NAV_TITLE: { [key in GroupSettingType]: string } =
    useMemo(
      () => ({
        home: t('setText'),
        list: isDiscussion ? t('discussionMemberText') : t('teamMemberText'),
        detail: isDiscussion ? t('discussionText') : t('teamInfoText'),
        power: t('teamPowerText'),
      }),
      [t, isDiscussion]
    )

  const handleStackPush = (path: GroupSettingType) => {
    setNavHistoryStack(
      navHistoryStack.concat({
        path,
        title: GROUP_SETTING_NAV_TITLE[path],
      })
    )
  }

  const handleChangeNickInTeam = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNickInTeam(e.target.value.trim())
  }

  const handleUpdateMyMemberInfo = () => {
    onUpdateMyMemberInfo({
      teamNick: nickInTeam,
    })
  }

  const showDismissConfirm = () => {
    confirm({
      title: t('dismissTeamText'),
      icon: <ExclamationCircleOutlined />,
      content: t('dismissTeamConfirmText'),
      okText: t('okText'),
      cancelText: t('cancelText'),
      onOk() {
        onDismissTeam()
      },
    })
  }

  const showLeaveConfirm = () => {
    confirm({
      title: isDiscussion ? t('leaveDiscussionTitle') : t('leaveTeamTitle'),
      icon: <ExclamationCircleOutlined />,
      content: isDiscussion
        ? t('leaveDiscussionConfirmText')
        : t('leaveTeamConfirmText'),
      okText: t('okText'),
      cancelText: t('cancelText'),
      onOk() {
        isDiscussion ? onLeaveDiscussion?.() : onLeaveTeam()
      },
    })
  }

  const isOwnerOrManager = isGroupOwner || isGroupManager

  const hasUpdateTeamPower = useMemo(() => {
    if (isDiscussion) {
      return true
    }

    if (
      team.updateInfoMode ===
        V2NIMConst.V2NIMTeamUpdateInfoMode
          .V2NIM_TEAM_UPDATE_INFO_MODE_MANAGER &&
      isOwnerOrManager
    ) {
      return true
    }

    return (
      team.updateInfoMode ===
      V2NIMConst.V2NIMTeamUpdateInfoMode.V2NIM_TEAM_UPDATE_INFO_MODE_ALL
    )
  }, [team.updateInfoMode, isOwnerOrManager, isDiscussion])

  const myMemberInfo = useMemo(() => {
    return (
      members.find((item) => item.accountId === myAccount) ||
      ({} as V2NIMTeamMember)
    )
  }, [myAccount, members])

  const teamManagers = useMemo(() => {
    return members.filter(
      (item) =>
        item.memberRole ===
        V2NIMConst.V2NIMTeamMemberRole.V2NIM_TEAM_MEMBER_ROLE_MANAGER
    )
  }, [members])

  useEffect(() => {
    setNickInTeam(myMemberInfo.teamNick || '')
  }, [myMemberInfo.teamNick])

  useEffect(() => {
    if (!navHistoryStack.length) {
      handleStackPush('home')
    }
  }, [navHistoryStack])

  return (
    <div className={`${_prefix}-wrap`}>
      {path === 'home' ? (
        <>
          <div
            className={`${_prefix}-head ${_prefix}-item`}
            onClick={handleStackPush.bind(null, 'detail')}
          >
            <div className={`${_prefix}-head-info`}>
              <CrudeAvatar
                account={team.teamId}
                nick={team.name}
                avatar={team.avatar}
              />
              <span className={`${_prefix}-label`}>{team.name}</span>
            </div>
            <RightOutlined size={10} />
          </div>
          <div className={`${_prefix}-members ${_prefix}-item`}>
            <div
              className={`${_prefix}-members-info`}
              onClick={handleStackPush.bind(null, 'list')}
            >
              <div>
                {isDiscussion ? (
                  <b>{t('discussionMemberText')}</b>
                ) : (
                  <b>{t('teamMemberText')}</b>
                )}
                <span className={`${_prefix}-members-num`}>
                  ({members.length}) {t('personUnit')}
                </span>
              </div>
              <RightOutlined size={10} />
            </div>
            <div className={`${_prefix}-members-list`}>
              <span
                className={`${_prefix}-add-icon`}
                onClick={onAddMembersClick}
              >
                <PlusOutlined style={{ fontSize: 14, color: '#C1C8D1' }} />
              </span>
              <span className={`${_prefix}-members-upload`}></span>
              {members.slice(0, 6).map((item) => {
                return (
                  <ComplexAvatarContainer
                    key={item.accountId}
                    prefix={commonPrefix}
                    account={item.accountId}
                    canClick={false}
                  />
                )
              })}
            </div>
          </div>
          {!isDiscussion && (
            <div className={`${_prefix}-item`}>
              <b>{t('nickInTeamText')}</b>
              <Input
                className={`${_prefix}-nickinteam`}
                value={nickInTeam}
                allowClear
                maxLength={15}
                onChange={handleChangeNickInTeam}
                onBlur={handleUpdateMyMemberInfo}
                placeholder={t('editNickInTeamText')}
              />
            </div>
          )}
          <div className={`${_prefix}-disturb ${_prefix}-item`}>
            <b>
              {isDiscussion
                ? t('discussionDoNotDisturbText')
                : t('teamDoNotDisturbText')}
            </b>
            <Switch
              checked={
                teamDoNotDisturbMode !==
                V2NIMConst.V2NIMTeamMessageMuteMode
                  .V2NIM_TEAM_MESSAGE_MUTE_MODE_OFF
              }
              onChange={onTeamDisturbChange}
            />
          </div>
          {team.teamType !== V2NIMConst.V2NIMTeamType.V2NIM_TEAM_TYPE_INVALID &&
          !isDiscussion &&
          isOwnerOrManager ? (
            <div
              className={`${_prefix}-power ${_prefix}-item`}
              onClick={handleStackPush.bind(null, 'power')}
            >
              <b>{t('teamPowerText')}</b>
              <RightOutlined size={10} />
            </div>
          ) : null}
        </>
      ) : (
        <>
          {path === 'detail' && (
            <GroupDetail
              team={team}
              hasPower={hasUpdateTeamPower}
              onUpdateTeamInfo={onUpdateTeamInfo}
              prefix={prefix}
              commonPrefix={commonPrefix}
            />
          )}
          {path === 'list' && (
            <GroupList
              isDiscussion={isDiscussion}
              members={members}
              myMemberInfo={myMemberInfo}
              onRemoveTeamMemberClick={onRemoveTeamMemberClick}
              afterSendMsgClick={afterSendMsgClick}
              renderTeamMemberItem={renderTeamMemberItem}
              prefix={prefix}
              commonPrefix={commonPrefix}
            />
          )}
          {path === 'power' && (
            <GroupPower
              onUpdateTeamInfo={onUpdateTeamInfo}
              onTeamMuteChange={onTeamMuteChange}
              onTeamDisturbChange={onTeamDisturbChange}
              team={team}
              teamDoNotDisturbMode={teamDoNotDisturbMode}
              managers={teamManagers}
              afterSendMsgClick={afterSendMsgClick}
              isGroupOwner={isGroupOwner}
              prefix={prefix}
            />
          )}
        </>
      )}
      {path === 'home' &&
        !isDiscussion &&
        (isGroupOwner ? (
          <div className={`${_prefix}-group-operation`}>
            {localOptions.allowTransferTeamOwner && (
              <Button
                className={`${_prefix}-group-operation-item`}
                onClick={onTransferTeamClick}
                danger
              >
                {t('transferOwnerText')}
              </Button>
            )}
            <Button
              danger
              className={`${_prefix}-group-operation-item ${_prefix}-group-operation-dismiss`}
              onClick={showDismissConfirm}
            >
              {t('dismissTeamText')}
            </Button>
          </div>
        ) : (
          <Button
            danger
            className={`${_prefix}-exit-btn`}
            onClick={showLeaveConfirm}
          >
            {t('leaveTeamButtonText')}
          </Button>
        ))}
      {path === 'home' && isDiscussion && (
        <div>
          <Button
            danger
            className={`${_prefix}-exit-btn`}
            onClick={showLeaveConfirm}
          >
            {t('leaveDiscussionButtonText')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default ChatTeamSetting
